import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { useSettings } from "@/context/SettingsContext";

export default function Privacy() {
  const { settings } = useSettings();
  const brand = settings?.brand_name || "Maison Pilet";
  const support = settings?.support_email || "hello@maisonpilet.com";

  return (
    <div className="min-h-screen bg-ivory" data-testid="privacy-page">
      <Navbar />
      <main className="max-w-3xl mx-auto px-6 lg:px-12 py-20">
        <span className="editorial-label">institutional · legal</span>
        <h1 className="font-serif text-5xl lg:text-6xl lowercase text-charcoal mt-3 mb-4">privacy policy.</h1>
        <p className="text-sm text-taupe mb-12">last updated · february 2026</p>

        <Section title="what we collect">
          <p>{brand} collects only the information necessary to operate the boutique:</p>
          <ul className="list-disc pl-6 mt-3 space-y-2">
            <li><span className="text-charcoal">email address</span> — when you subscribe to the waitlist or place an order</li>
            <li><span className="text-charcoal">shipping address & contact details</span> — when you place an order</li>
            <li><span className="text-charcoal">payment information</span> — processed exclusively by Stripe (PCI-compliant); we never see card numbers</li>
            <li><span className="text-charcoal">fit profile (optional)</span> — height, weight, and measurements you choose to share to receive size-matched recommendations</li>
            <li><span className="text-charcoal">browsing analytics</span> — anonymous page views to understand what pieces resonate</li>
          </ul>
        </Section>

        <Section title="how we use it">
          <p>Your data is used to:</p>
          <ul className="list-disc pl-6 mt-3 space-y-2">
            <li>process and fulfil orders</li>
            <li>notify you when a piece in your size lands (waitlist)</li>
            <li>respond to your enquiries</li>
            <li>improve the editorial experience</li>
          </ul>
          <p className="mt-4">We do not sell, rent, or trade your information.</p>
        </Section>

        <Section title="cross-posting & third parties">
          <p>{brand} cross-posts certain pieces to <strong>eBay</strong> and <strong>Instagram</strong> for visibility. When this happens, only the public-facing piece information (title, photos, price, condition, measurements) is shared — never your personal data. Buyer information on those platforms is governed by each platform&apos;s own policy.</p>
          <p className="mt-4">Other processors we rely on:</p>
          <ul className="list-disc pl-6 mt-3 space-y-2">
            <li><span className="text-charcoal">Stripe</span> — payment processing</li>
            <li><span className="text-charcoal">Resend</span> — transactional email</li>
            <li><span className="text-charcoal">USPS / DHL / FedEx</span> — shipping carriers</li>
          </ul>
        </Section>

        <Section title="cookies">
          <p>We use a minimal set of cookies and local storage entries: authentication session (for admin), shopping cart contents, and (with your permission) your fit profile. No third-party advertising or tracking cookies.</p>
        </Section>

        <Section title="your rights">
          <p>You may at any time request a copy of the personal data we hold about you, or ask us to delete it. Write to <a href={`mailto:${support}`} className="text-brass underline">{support}</a> from the email on file and we will respond within thirty (30) days.</p>
        </Section>

        <Section title="data retention">
          <p>Order records are retained for seven (7) years to satisfy tax and accounting requirements (IRS &amp; Georgia Department of Revenue). Marketing email addresses are kept until you unsubscribe. Anonymous analytics are retained for thirteen (13) months.</p>
        </Section>

        <Section title="contact">
          <p>{brand}<br />
          Acworth, Georgia, United States<br />
          <a href={`mailto:${support}`} className="text-brass underline">{support}</a></p>
        </Section>

        <p className="text-xs text-taupe italic mt-12">This page is provided as a baseline privacy notice for a sole-proprietor resale boutique. It is not legal advice. Please have a Georgia-licensed attorney review and refine the language for your specific operation, especially if you grow to handle EU residents (GDPR), California residents (CCPA/CPRA), or expand the integrations beyond those listed.</p>
      </main>
      <Footer />
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="mb-10">
      <h2 className="font-serif text-2xl lowercase text-charcoal mb-3">{title}</h2>
      <div className="text-sm text-espresso leading-relaxed space-y-2">{children}</div>
    </section>
  );
}
